// Claude Bridge — generates prompts for claude.ai browser, processes pasted responses.
// Flow: JARVIS builds prompt → user copies → pastes into claude.ai → pastes response back → JARVIS learns.

import {
  buildMarketAnalysisPrompt,
  buildDailyLearningPrompt,
  buildKnowledgeExtractionPrompt,
  buildTradeReviewPrompt,
  buildWeeklyBridgePrompt,
} from '../prompts/bridgePrompts';

import {
  getFullJarvisContext,
  saveRule,
  saveBehavior,
  savePattern,
  saveDailyLesson,
  updateJarvisMemory,
} from './storage';

// ─── Prompt types ─────────────────────────────────────────────────────
export const BRIDGE_TYPES = {
  MARKET_ANALYSIS:      'market_analysis',
  DAILY_LEARNING:       'daily_learning',
  KNOWLEDGE_EXTRACTION: 'knowledge_extraction',
  TRADE_REVIEW:         'trade_review',
  WEEKLY_BRIEFING:      'weekly_briefing',
};

export const BRIDGE_TYPE_LABELS = {
  [BRIDGE_TYPES.MARKET_ANALYSIS]:      'Deep Market Analysis',
  [BRIDGE_TYPES.DAILY_LEARNING]:       'Daily Learning (Recap)',
  [BRIDGE_TYPES.KNOWLEDGE_EXTRACTION]: 'Knowledge Extraction',
  [BRIDGE_TYPES.TRADE_REVIEW]:         'Trade Review',
  [BRIDGE_TYPES.WEEKLY_BRIEFING]:      'Weekly Briefing',
};

// ─── Generate prompt for Claude browser ──────────────────────────────
// Returns a formatted string the user copies into claude.ai.
export const generateBridgePrompt = (type, payload = {}) => {
  const context = getFullJarvisContext();

  switch (type) {
    case BRIDGE_TYPES.MARKET_ANALYSIS:
      return buildMarketAnalysisPrompt(context, payload.question || '');

    case BRIDGE_TYPES.DAILY_LEARNING:
      return buildDailyLearningPrompt(payload.recap || {}, context);

    case BRIDGE_TYPES.KNOWLEDGE_EXTRACTION:
      return buildKnowledgeExtractionPrompt(payload.content || '', payload.contentType || 'article');

    case BRIDGE_TYPES.TRADE_REVIEW:
      return buildTradeReviewPrompt(payload.trade || {}, context);

    case BRIDGE_TYPES.WEEKLY_BRIEFING:
      return buildWeeklyBridgePrompt(payload.weekData || {}, context);

    default:
      throw new Error(`Unknown bridge type: ${type}`);
  }
};

// ─── Process pasted Claude response ──────────────────────────────────
// Called when user pastes Claude's reply back into JARVIS.
// Extracts and saves all learnings automatically.
// Returns { saved, summary } so the UI can show what was learned.
export const processBridgeResponse = (type, rawResponse) => {
  // Try to parse as JSON first — all bridge prompts return JSON
  let data = null;
  const clean = rawResponse
    .trim()
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/,      '')
    .replace(/```\s*$/,      '');

  try {
    data = JSON.parse(clean);
  } catch {
    // Not JSON — wrap as free-text for manual review
    return processFreeTextResponse(rawResponse);
  }

  const saved = {
    rules:     0,
    behaviors: 0,
    patterns:  0,
    lessons:   0,
    ict:       0,
  };

  switch (type) {
    case BRIDGE_TYPES.MARKET_ANALYSIS:
      saved.rules     += saveExtractedRules(data.rules_to_add || []);
      saved.behaviors += saveExtractedBehaviors(data.behaviors_to_add || []);
      saved.patterns  += saveExtractedPatterns(data.patterns_to_add || []);
      break;

    case BRIDGE_TYPES.DAILY_LEARNING:
      saved.behaviors += saveExtractedBehaviors(data.behaviors_new || []);
      saved.patterns  += saveExtractedPatterns(data.patterns_new  || []);
      saved.rules     += saveExtractedRules(data.rules_new        || []);
      if (data.key_takeaway || data.summary) {
        saveDailyLesson({
          date: new Date().toISOString().split('T')[0],
          jarvis_summary:  data.summary,
          jarvis_takeaway: data.key_takeaway,
          tomorrow_watch:  data.tomorrow_watch,
          bridge_sourced:  true,
        });
        saved.lessons++;
      }
      break;

    case BRIDGE_TYPES.KNOWLEDGE_EXTRACTION:
      saved.rules     += saveExtractedRules(data.rules_to_add     || []);
      saved.behaviors += saveExtractedBehaviors(data.behaviors_to_add || []);
      saved.patterns  += saveExtractedPatterns(data.patterns_to_add  || []);
      break;

    case BRIDGE_TYPES.TRADE_REVIEW:
      if (data.new_rule_from_trade?.should_add && data.new_rule_from_trade?.title) {
        saveRule({
          title:       data.new_rule_from_trade.title,
          description: data.new_rule_from_trade.description,
          action:      data.new_rule_from_trade.action || 'ALWAYS',
          source:      'Claude Bridge — Trade Review',
          auto_extracted: true,
        });
        saved.rules++;
      }
      break;

    case BRIDGE_TYPES.WEEKLY_BRIEFING:
      saved.rules     += saveExtractedRules(data.rules_to_add  || []);
      saved.behaviors += saveExtractedBehaviors(data.behaviors_new || []);
      // Save weekly briefing to JARVIS memory
      updateJarvisMemory({
        last_weekly_briefing: {
          ...data,
          generated_at: new Date().toISOString(),
        },
      });
      break;

    default:
      break;
  }

  const total = saved.rules + saved.behaviors + saved.patterns + saved.lessons;

  return {
    success: true,
    saved,
    total,
    summary: buildSavedSummary(saved, data),
    data,
  };
};

// ─── Free-text fallback ───────────────────────────────────────────────
// If Claude responded in prose (not JSON), store as a raw lesson.
const processFreeTextResponse = (text) => {
  saveDailyLesson({
    date: new Date().toISOString().split('T')[0],
    market_behavior: 'Claude Bridge — Free text response',
    notes: text.substring(0, 2000),
    bridge_sourced: true,
    raw_response: true,
  });

  return {
    success:   true,
    saved:     { rules: 0, behaviors: 0, patterns: 0, lessons: 1, ict: 0 },
    total:     1,
    summary:   'Saved as raw lesson for manual review (Claude responded in prose, not JSON)',
    data:      null,
  };
};

// ─── Batch save helpers ───────────────────────────────────────────────
const saveExtractedRules = (rules) => {
  let count = 0;
  rules.forEach(r => {
    if (r?.title && r?.description) {
      saveRule({ ...r, source: 'Claude Bridge', auto_extracted: true });
      count++;
    }
  });
  return count;
};

const saveExtractedBehaviors = (behaviors) => {
  let count = 0;
  behaviors.forEach(b => {
    if (b?.title) {
      saveBehavior({ ...b, learned_from: 'Claude Bridge', confidence: b.confidence || 'LOW' });
      count++;
    }
  });
  return count;
};

const saveExtractedPatterns = (patterns) => {
  let count = 0;
  patterns.forEach(p => {
    if (p?.title) {
      savePattern({ ...p, auto_discovered: true, confidence: p.confidence || 'LOW' });
      count++;
    }
  });
  return count;
};

// ─── Summary string for the UI ────────────────────────────────────────
const buildSavedSummary = (saved, data) => {
  const parts = [];
  if (saved.rules     > 0) parts.push(`${saved.rules} rule${saved.rules     > 1 ? 's' : ''}`);
  if (saved.behaviors > 0) parts.push(`${saved.behaviors} behavior${saved.behaviors > 1 ? 's' : ''}`);
  if (saved.patterns  > 0) parts.push(`${saved.patterns} pattern${saved.patterns  > 1 ? 's' : ''}`);
  if (saved.lessons   > 0) parts.push(`${saved.lessons} lesson${saved.lessons   > 1 ? 's' : ''}`);

  if (parts.length === 0) {
    return data?.jarvis_verdict || data?.summary || 'Processed — no new items to save.';
  }

  return `JARVIS learned: ${parts.join(', ')} saved to knowledge base.`;
};
